import { useEffectWithController } from '@carvana/merch-react-hooks';
import { useContext } from 'react';
import useIsInReturnUserExperiment from '@/home/hooks/useIsInReturnUserExperiment';
import { httpWithAuth } from '~/clients';
import { GET_DISPLAYABLE_FAVORITES } from '~/constants/endpoints';
import { InventoryTypeKey } from '~/constants/inventoryType';
import UserContext, { useIsLoggedIn } from '~/context/common/UserContext';
import { VehicleTag } from '~/services/common/contracts/InventoryResponse';
import { generateErrorLogger } from '~/utilities/loggers';

export type DisplayableFavorite = {
  jellyBeanMobileUrl: string;
  locationId: number;
  make: string;
  mileage: number;
  model: string;
  previousPrice?: number;
  price: {
    total: number;
    totalDiscountAmount: number;
  };
  saleStatus: string;
  stockNumber: number;
  trim: string;
  vehicleId: number;
  vehicleInventoryType: InventoryTypeKey;
  vehiclePurchaseType: string;
  vehicleReservableReasons: string[];
  vehicleTags: VehicleTag[];
  vehicleUnlockDateTime?: Date;
  year: number;
};

export default function useGetDisplayableFavorites() {
  const {
    displayableFavorites: {
      setData,
      data: displayableFavorites,
      isLoaded,
      setError
    }
  } = useContext(UserContext);
  const isLoggedIn = useIsLoggedIn();
  const isInExperiment = useIsInReturnUserExperiment();

  const shouldFetchFavorites = isLoggedIn && isInExperiment;

  useEffectWithController(
    controller => {
      if (!shouldFetchFavorites) {
        setData([]);

        return;
      }

      httpWithAuth
        .get<DisplayableFavorite[]>(GET_DISPLAYABLE_FAVORITES)
        .then(({ data }) => setData([...data].reverse()))
        .catch(
          generateErrorLogger(
            'useGetDisplayableFavorites',
            {},
            { callback: setError, controller }
          )
        );
    },
    [isLoggedIn, setData, setError, shouldFetchFavorites]
  );

  return { displayableFavorites, isLoaded };
}
